import { useMemo } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Table, Reservation } from "@/types/table";
import { Users, MapPin, User as UserIcon, Clock } from "lucide-react";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";

interface ZoneLite { id: string; name: string; color?: string }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tables: Table[];
  zones: ZoneLite[];
  /** Day reservations per tableId (assigned tables only). */
  tableDayReservations: Map<string, Reservation[]>;
  /** All reservations for the selected day (incl. unassigned/en_attente). */
  dayReservations?: Reservation[];
  /** Selected day, used to show context in the palette. */
  selectedDate?: Date | null;
  /**
   * Called when user picks an item.
   * - If `tableId` is set → focus the table (and switch zone via `targetZoneId`).
   * - If `tableId` is null → open the reservation preview via `reservationId`.
   */
  onSelect: (
    tableId: string | null,
    targetZoneId: string,
    reservationId?: string,
  ) => void;
}

const fmtDate = (d?: Date | null) => {
  if (!d) return '';
  try {
    return d.toLocaleDateString('fr-CH', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch { return ''; }
};

/**
 * ⌘K / `/` palette — jump to a table or directly to a client of the selected day.
 */
export const V2TableSearchPalette = ({
  open, onOpenChange, tables, zones, tableDayReservations,
  dayReservations = [], selectedDate, onSelect,
}: Props) => {
  const compareServiceTime = useServiceTimeCompare();
  const zoneById = useMemo(() => new Map(zones.map(z => [z.id, z])), [zones]);
  const tableById = useMemo(() => new Map(tables.map(t => [t.id, t])), [tables]);

  // Tables rows — aggregate ALL day reservations into keywords
  const tableRows = useMemo(() => {
    return tables.map(t => {
      const zone = t.zoneId ? zoneById.get(t.zoneId) : undefined;
      const dayRes = (tableDayReservations.get(t.id) ?? [])
        .filter(r => r.status !== 'annulé')
        .sort((a, b) => compareServiceTime(a.startTime, b.startTime));
      const next = dayRes[0];
      const clientLabel = next
        ? `${next.startTime.substring(0, 5)} · ${next.customerFirstName ?? ''} ${next.customerLastName ?? ''}`.trim()
        : null;
      const clientKeywords: string[] = [];
      for (const r of dayRes) {
        if (r.customerFirstName) clientKeywords.push(r.customerFirstName);
        if (r.customerLastName) clientKeywords.push(r.customerLastName);
        if ((r as any).customerPhone) clientKeywords.push((r as any).customerPhone);
        if ((r as any).customerEmail) clientKeywords.push((r as any).customerEmail);
      }
      return {
        table: t,
        zoneName: zone?.name ?? 'Sans zone',
        zoneColor: zone?.color,
        clientLabel,
        keywords: [t.name, zone?.name ?? '', ...clientKeywords].filter(Boolean) as string[],
      };
    });
  }, [tables, zoneById, tableDayReservations, compareServiceTime]);

  // Client rows — one entry per reservation of the day (incl. en_attente)
  const clientRows = useMemo(() => {
    const rows = dayReservations
      .filter(r => r.status !== 'annulé')
      .map(r => {
        const t = r.tableId ? tableById.get(r.tableId) : undefined;
        const zone = t?.zoneId ? zoneById.get(t.zoneId) : undefined;
        const tableLabel = t?.name
          ?? ((r as any).combinedTableId ? 'Table combinée' : 'Sans table');
        const fullName = `${r.customerFirstName ?? ''} ${r.customerLastName ?? ''}`.trim() || '— sans nom —';
        return {
          reservation: r,
          tableId: t?.id ?? null,
          targetZoneId: t?.zoneId ?? '__all__',
          zoneColor: zone?.color,
          fullName,
          tableLabel,
          time: r.startTime?.substring(0, 5) ?? '',
          value: [
            fullName,
            r.customerFirstName ?? '',
            r.customerLastName ?? '',
            (r as any).customerPhone ?? '',
            (r as any).customerEmail ?? '',
            tableLabel,
            r.startTime ?? '',
            String(r.numberOfPeople ?? ''),
          ].filter(Boolean).join(' '),
        };
      })
      .sort((a, b) => compareServiceTime(a.time, b.time));
    return rows;
  }, [dayReservations, tableById, zoneById, compareServiceTime]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Rechercher un client, une table, une zone… (jour sélectionné)" />
      {selectedDate && (
        <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b">
          Résultats du {fmtDate(selectedDate)}
        </div>
      )}
      <CommandList>
        <CommandEmpty>Aucun résultat.</CommandEmpty>

        {clientRows.length > 0 && (
          <CommandGroup heading={`Clients · ${clientRows.length}`}>
            {clientRows.map(r => (
              <CommandItem
                key={`res-${r.reservation.id}`}
                value={`client ${r.value}`}
                onSelect={() => {
                  onSelect(r.tableId, r.targetZoneId, r.reservation.id);
                  onOpenChange(false);
                }}
                className="flex items-center gap-2"
              >
                <UserIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="flex items-center gap-1 text-xs font-mono w-12 shrink-0">
                  <Clock className="w-3 h-3" /> {r.time}
                </span>
                <span className="font-medium truncate">{r.fullName}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto shrink-0">
                  <Users className="w-3 h-3" /> {r.reservation.numberOfPeople}p
                </span>
                <span
                  className="flex items-center gap-1 text-xs text-muted-foreground shrink-0"
                  title={r.tableLabel}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: r.zoneColor || 'hsl(var(--muted-foreground))' }}
                  />
                  {r.tableLabel}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading={`Tables · ${tableRows.length}`}>
          {tableRows.map(r => (
            <CommandItem
              key={`tbl-${r.table.id}`}
              value={`table ${r.table.name} ${r.keywords.join(' ')}`}
              onSelect={() => {
                onSelect(r.table.id, r.table.zoneId ?? '__all__');
                onOpenChange(false);
              }}
              className="flex items-center gap-2"
            >
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: r.zoneColor || 'hsl(var(--muted-foreground))' }}
              />
              <span className="font-semibold w-14 shrink-0 truncate">{r.table.name}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <MapPin className="w-3 h-3" /> {r.zoneName}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <Users className="w-3 h-3" /> {r.table.capacity}p
              </span>
              {r.clientLabel && (
                <span className="text-xs text-foreground/80 ml-auto truncate max-w-[40%]">
                  → {r.clientLabel}
                </span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
